import { useEffect, useState } from 'react';
import { Grid, Group, Loader, Paper, ScrollArea, SegmentedControl, Stack, Text, TextInput } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { unwrap } from '../api/client';
import { getConversation, me } from '../api/gen';
import { useOrg } from '../api/org';
import { ConversationList, VIEWS, type View } from '../components/ConversationList';
import { DraftPanel } from '../components/DraftPanel';
import { NotePanel } from '../components/NotePanel';
import { ThreadHeader } from '../components/ThreadHeader';
import { ThreadView } from '../components/ThreadView';
import { listen } from '../lib/stream';
import { useSession } from '../lib/session';

// The screen this product is. A list of threads on the left, one thread on the
// right, and the draft under it — which is the arrangement every inbox has,
// because it is the one that works.
//
// Nothing polls: the live feed invalidates, and the queries re-fetch.
export function InboxPage() {
  const org = useOrg();
  const qc = useQueryClient();
  const { orgId } = useSession();

  const [view, setView] = useState<View>('waiting');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  // The organisation, not the header object: `useOrg` builds a fresh one every
  // render, and depending on it would tear the stream down and open a new one
  // on every keystroke.
  useEffect(() => listen(qc, { 'X-Org-ID': orgId! }), [qc, orgId]);

  const who = useQuery({ queryKey: ['me'], queryFn: () => unwrap(me()) });
  const memberId = who.data?.memberships?.find((m) => m.orgId === orgId)?.memberId;

  const conversation = useQuery({
    queryKey: ['conversation', selected],
    queryFn: () => unwrap(getConversation({ path: { id: selected! }, headers: org })),
    enabled: !!selected,
  });

  return (
    // The inner row takes the grid's height too, or each column's 100% is
    // its content's and the page scrolls instead of the list or the thread.
    <Grid gap={0} h="calc(100vh - 52px)" styles={{ inner: { height: '100%' } }}>
      <Grid.Col span={{ base: 12, sm: 4, lg: 3 }} h="100%" style={{ borderRight: '1px solid var(--mantine-color-gray-3)' }}>
        <Stack gap={0} h="100%">
          <Stack gap="xs" p="sm">
            <SegmentedControl
              size="xs"
              fullWidth
              value={view}
              onChange={(v) => setView(v as View)}
              data={VIEWS}
            />
            <TextInput
              size="xs"
              placeholder="Search subjects"
              leftSection={<IconSearch size={14} />}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
            />
          </Stack>
          <div style={{ flex: 1, minHeight: 0 }}>
            <ConversationList
              view={view}
              search={search}
              selected={selected}
              onSelect={setSelected}
              memberId={memberId}
            />
          </div>
        </Stack>
      </Grid.Col>

      <Grid.Col span={{ base: 12, sm: 8, lg: 9 }} h="100%">
        {!selected ? (
          <Group justify="center" h="100%">
            <Text c="dimmed" size="sm">
              Pick a conversation.
            </Text>
          </Group>
        ) : conversation.isLoading || !conversation.data ? (
          <Group justify="center" h="100%">
            <Loader size="sm" />
          </Group>
        ) : (
          <Stack gap={0} h="100%">
            <Paper p="sm" withBorder radius={0}>
              <ThreadHeader conversation={conversation.data} />
            </Paper>
            {/* minHeight 0 lets the thread shrink and scroll inside the
                column; without it the reply box is pushed below the window. */}
            <ScrollArea style={{ flex: 1, minHeight: 0 }} p="md">
              <Stack gap="md" maw={820}>
                <ThreadView conversationId={selected} />
                <NotePanel conversationId={selected} />
              </Stack>
            </ScrollArea>
            <Paper p="sm" withBorder radius={0}>
              <div style={{ maxWidth: 820 }}>
                <DraftPanel conversationId={selected} />
              </div>
            </Paper>
          </Stack>
        )}
      </Grid.Col>
    </Grid>
  );
}
